import { AgentChatMessage } from "@coeff/api";

export type Message = AgentChatMessage;

/**
 * Check if a message is from the user (vs agent/event).
 */
export function isUserMessage(message: Message): boolean {
  return message.role === "user";
}

/**
 * Check if a message contains a dashboard modification. The server sends the full
 * `web_dashboard_json` only for the current turn; older turns carry `dashboard_updated`.
 */
export function hasDashboardModification(message: Message): boolean {
  return Boolean(
    message.content?.dashboard_updated || message.content?.web_dashboard_json?.content_config
  );
}

/**
 * Get the action label for a message, with fallback.
 */
export function getActionLabel(message: Message, fallback = "Updated dashboard"): string {
  return message.content?.tool_call_summary_label || fallback;
}

/**
 * Extract and trim message text.
 */
export function getMessageText(message: Message): string {
  if (message.content?.text) {
    return message.content.text.trim();
  }

  if (message.content?.reasoning) {
    return message.content.reasoning.trim();
  }

  if (message.content?.tool_call_summary_label) {
    return message.content.tool_call_summary_label.trim();
  }

  return "";
}

/**
 * Theme flows put ids in the message text for the agent's tools: the request card appends
 * "(theme request id: …)" and the selector's Edit prefills "(theme id: …)". The ids are for the
 * tool, not the reader, so the chat bubble renders the sentence without them.
 */
const THEME_ID_REFERENCE = /\s*\((?:theme|theme request) id: [0-9a-f-]{36}\)/gi;

export function hasThemeIdReference(message: Message): boolean {
  // A fresh regex each call: THEME_ID_REFERENCE is global, so `test` would advance lastIndex.
  return new RegExp(THEME_ID_REFERENCE.source, "i").test(getMessageText(message));
}

export function stripThemeIdReferences(text: string): string {
  return text
    .replace(THEME_ID_REFERENCE, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}
